import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
  } from "react";
  import { useDispatch, useSelector } from "react-redux";
  import Aggrid from "../../framework/AgGrid";
  import Button from "../../framework/Button";
  import CellNumberEditor from "../../framework/cellEditor/CellNumberEditor";
  import CellSelectInput from "../../framework/cellEditor/CellSelectInput";
  import CellTextEditor from "../../framework/cellEditor/CellTextEditor";
  import CellTreeSelect from "../../framework/cellEditor/CellTreeSelect";
  import GlobalContext from "../../framework/GlobalContext";
  import Shortcut from "../../framework/Shortcut";
  import { displayToast } from "../../framework/toast/toastUtils";
  import {
    cellTabbed,
    debounceFocusCell,
    focusCell,
    rowDown,
    rowUp,
    selectRow,
    stopEditing,
  } from "../../utils/gridUtils";
  import { getStringValue } from "../../utils/utils";
  import { newDoctorCommissionProgram } from "./DoctorCommissionProgramConstants";
  import DoctorCommissionProgramContext from "./DoctorCommisionProgramContext";
  import classes from "./DoctorCommissionProgramGrid.module.css";
  
  const DoctorCommissionProgramGrid = () => {
    const [api, setApi] = useState(null);
    const [columnApi, setColumnApi] = useState(null);
    const {
      doctorCommissionProgramGridRef,
      addClicked,
      setAddClicked,
      addClickedRef,
    } = useContext(DoctorCommissionProgramContext);
  
    const { setCurrentComponent, currentComponent } = useContext(GlobalContext);
  
    const { doctorCommissionProgramRowData, gridColumns } = useSelector(
      (state) => state.commonReducer.tabs[state.commonReducer.activeTabIndex]
    );
  
    const dispatch = useDispatch();
  
    const onGridReady = (params) => {
      setApi(params.api);
      setColumnApi(params.columnApi);
      params.api.sizeColumnsToFit();
    };
  
    const addProgram = useCallback(() => {
      let updatedDoctorCommissionProgram = { ...newDoctorCommissionProgram };
      let rowData = [...doctorCommissionProgramRowData];
      if (rowData.length > 0) {
        updatedDoctorCommissionProgram = {
          ...updatedDoctorCommissionProgram,
        };
  
        // Set min value to previous row's max + 1
        const previousRow = rowData[rowData.length - 1];
        if (
          previousRow &&
          previousRow.max !== undefined &&
          previousRow.max !== null
        ) {
          updatedDoctorCommissionProgram.min = previousRow.max + 1;
          updatedDoctorCommissionProgram.max = previousRow.max + 2;
        }
      }
      rowData = [
        ...doctorCommissionProgramRowData,
        updatedDoctorCommissionProgram,
      ];
      dispatch({
        type: "update_doctor_commission_program_grid",
        payload: rowData,
      });
      setTimeout(() => {
        selectRow(api, rowData.length - 1);
        debounceFocusCell(api, rowData.length - 1, columnApi?.getColumn("min"));
      }, 100);
      setAddClicked(false);
    }, [api, columnApi, dispatch, setAddClicked, doctorCommissionProgramRowData]);
  
    const removeProduct = useCallback(() => {
      if (api?.getSelectedNodes()?.length === 0) {
        return displayToast({
          type: "warn",
          title: "Please select a doctor commission program to delete.",
        });
      }
      let rowData = [...doctorCommissionProgramRowData];
      let selectedIndex = api.getSelectedNodes()[0].rowIndex;
      rowData.splice(selectedIndex, 1);
      if (rowData.length === 0) {
        rowData.push(newDoctorCommissionProgram);
      } else {
        // Ensure first row always has min = 0
        if (rowData[0]) {
          rowData[0] = { ...rowData[0], min: 0 };
        }
        // Update min values for subsequent rows based on previous row's max
        for (let i = 1; i < rowData.length; i++) {
          const previousRow = rowData[i - 1];
          if (
            previousRow &&
            previousRow.max !== undefined &&
            previousRow.max !== null
          ) {
            rowData[i] = { ...rowData[i], min: previousRow.max + 1 };
          }
        }
      }
      dispatch({
        type: "update_doctor_commission_program_grid",
        payload: [...rowData],
      });
      if (rowData.length === 0) return;
      selectRow(
        api,
        selectedIndex === rowData.length ? selectedIndex - 1 : selectedIndex
      );
      debounceFocusCell(
        api,
        selectedIndex === rowData.length ? selectedIndex - 1 : selectedIndex,
        columnApi?.getColumn("min")
      );
    }, [api, columnApi, dispatch, doctorCommissionProgramRowData]);
  
    const mainGridMarkers = useMemo(() => {
      return {
        altDown: "doctor_commission_program_manufacturer_group",
        altUp: "doctor_commission_program_manufacturer_group",
        left: "grid_doctor_commission_program",
        right: "grid_doctor_commission_program",
        down: "doctor_commission_program_manufacturer_group",
        up: "doctor_commission_program_manufacturer_group",
      };
    }, []);
    const gridEditingStopped = useCallback(
      (direction) => {
        if (api) stopEditing(api);
        if (currentComponent === "grid_doctor_commission_program") {
          const rowIndex =
            direction === "right" ? 0 : api.getDisplayedRowCount() - 1;
          selectRow(api, rowIndex);
          return doctorCommissionProgramGridRef.current.focus(
            direction !== "right",
            "min"
          );
        }
  
        if (direction && mainGridMarkers[direction]) {
          document
            .getElementById(mainGridMarkers[direction])
            .focus({ preventScroll: true });
          setCurrentComponent(mainGridMarkers[direction]);
        }
      },
      [
        api,
        currentComponent,
        mainGridMarkers,
        doctorCommissionProgramGridRef,
        setCurrentComponent,
      ]
    );
    const defaultColDef = {
      flex: 1,
      cellStyle: (params) => {
        return {
          fontSize: 14,
          fontWeight: 500,
        };
      },
      cellEditor: "cellTextEditor",
      cellEditorParams: (params) => {
        return {
          value: params?.value || "",
        };
      },
      cellRendererFramework: (params) => {
        return params.colDef.editable ? (
          <div className="ag-editable-cell">
            <div>{getStringValue(params.value)}</div>
          </div>
        ) : (
          getStringValue(params.value)
        );
      },
      suppressKeyboardEvent: (params) => true,
      editable: true,
    };
    const tabHandler = useCallback(
      (shiftKey) => {
        if (!cellTabbed(api, shiftKey)) {
          gridEditingStopped(shiftKey ? "up" : "right");
        }
      },
      [api, gridEditingStopped]
    );
  
    const cellValueChanged = useCallback(
      (node, changedField) => {
        let updatedRowData = {};
        let updatedReduxData = [];
        updatedRowData = node.data;
        updatedReduxData = [...doctorCommissionProgramRowData];
        const currentRowIndex = node.rowIndex;
        updatedReduxData[currentRowIndex] = updatedRowData;
  
        // Ensure first row always has min = 0
        if (currentRowIndex === 0 && changedField === "min") {
          updatedRowData.min = 0;
          updatedReduxData[currentRowIndex] = updatedRowData;
          dispatch({
            type: "update_doctor_commission_program_grid",
            payload: [...updatedReduxData],
          });
          return;
        }
  
        // Validate min and max values for subsequent rows
        if (currentRowIndex > 0) {
          const previousRow = updatedReduxData[currentRowIndex - 1];
          if (
            previousRow &&
            previousRow.max !== undefined &&
            previousRow.max !== null
          ) {
            // Validate min value - must be greater than previous row's max
            if (changedField === "min") {
              const currentMin = node.data.min;
              if (currentMin <= previousRow.max) {
                displayToast({
                  type: "error",
                  title: `Min value must be greater than previous row's max value.`,
                });
                const expectedMin = previousRow.max + 1;
                updatedRowData.min = expectedMin;
                updatedReduxData[currentRowIndex] = updatedRowData;
                dispatch({
                  type: "update_doctor_commission_program_grid",
                  payload: [...updatedReduxData],
                });
                return;
              }
            }
            // Validate max value - must be greater than min in the same row
            if (changedField === "max") {
              const currentMin = updatedRowData.min !== undefined ? updatedRowData.min : node.data.min;
              const currentMax = node.data.max;
              if (currentMax <= currentMin) {
                displayToast({
                  type: "error",
                  title: `Max value must be greater than min value.`,
                });
                const expectedMax = currentMin + 1;
                updatedRowData.max = expectedMax;
                updatedReduxData[currentRowIndex] = updatedRowData;
                dispatch({
                  type: "update_doctor_commission_program_grid",
                  payload: [...updatedReduxData],
                });
                return;
              }
            }
          }
        } else if (currentRowIndex === 0) {
          // Validate max value for first row - must be greater than min (which is 0)
          if (changedField === "max") {
            const currentMax = node.data.max;
            const currentMin = updatedRowData.min !== undefined ? updatedRowData.min : node.data.min;
            if (currentMax <= currentMin) {
              displayToast({
                type: "error",
                title: `Max value must be greater than min value.`,
              });
              const expectedMax = currentMin + 1;
              updatedRowData.max = expectedMax;
              updatedReduxData[currentRowIndex] = updatedRowData;
              dispatch({
                type: "update_doctor_commission_program_grid",
                payload: [...updatedReduxData],
              });
              return;
            }
          }
        }
  
        dispatch({
          type: "update_doctor_commission_program_grid",
          payload: [...updatedReduxData],
        });
        updatedRowData = {};
        updatedReduxData = [];
      },
      [dispatch, doctorCommissionProgramRowData]
    );
  
    const gridListener = useCallback(
      (e) => {
        if (!api) return;
        const key = e.key;
        const shiftKey = e.shiftKey;
        const altKey = e.altKey;
        const ctrlKey =
          api.getSelectedNodes()[0]?.rowIndex === 0 ? false : e.ctrlKey;
  
        const isAutocompleteOpen =
          document.getElementsByClassName("MuiAutocomplete-listbox").length > 0;
        const isTreeSelectOpen =
          document.getElementsByClassName("tree-select-popper").length > 0;
        const isDropdownOpen = isAutocompleteOpen || isTreeSelectOpen;
  
        if (key === "Tab") {
          tabHandler(shiftKey);
          return;
        }
        if (key === "Enter") {
          tabHandler(shiftKey);
          return;
        }
        if (key === "ArrowDown") {
          if (altKey) {
            gridEditingStopped("altDown");
            return;
          }
  
          if (isDropdownOpen) {
            return;
          }
  
          const lastRowIndex = api.getDisplayedRowCount() - 1;
          const currentRowIndex = api.getFocusedCell()?.rowIndex;
  
          if (currentRowIndex === lastRowIndex) {
            document
              .getElementById("doctor_commission_program_manufacturer_group")
              .focus({ preventScroll: true });
            setCurrentComponent("doctor_commission_program_manufacturer_group");
            return;
          }
  
          if (api.getFocusedCell().column.colDef.editable) {
            if (!rowDown(api, ctrlKey, api.getFocusedCell().column))
              gridEditingStopped("down");
            return;
          }
          if (!rowDown(api, ctrlKey, columnApi.getColumn("category")))
            gridEditingStopped("down");
          return;
        }
        if (key === "ArrowUp") {
          if (altKey) {
            gridEditingStopped("altUp");
            return;
          }
          if (isDropdownOpen) {
            return;
          }
          if (api.getFocusedCell().column.colDef.editable) {
            if (!rowUp(api, ctrlKey, api.getFocusedCell().column))
              gridEditingStopped("up");
            return;
          }
          if (!rowUp(api, ctrlKey, columnApi.getColumn("category")))
            gridEditingStopped("up");
          return;
        }
        if (key === "Delete") {
          removeProduct();
          return;
        }
      },
      [
        api,
        columnApi,
        gridEditingStopped,
        removeProduct,
        tabHandler,
        setCurrentComponent,
      ]
    );
  
    useEffect(() => {
      if (addClicked) {
        addProgram();
      }
    }, [addClicked, addProgram]);
  
    useEffect(() => {
      if (api && gridColumns && gridColumns.length > 0) {
        const newGridColumns = gridColumns.map((column) => {
          if (column.field === "min") {
            return {
              ...column,
              cellEditor: "cellNumberEditor",
              editable: (params) => {
                const rowIndex = params.node.rowIndex;
                const rowData = params.api.getDisplayedRowAtIndex(rowIndex).data;
                if (rowIndex === 0) {
                  return false;
                }
                return true;
              },
              cellEditorParams: (params) => {
                return {
                  value: params?.value || 0,
                  onChange: (values) => {
                    params.node.data[column.field] = values.floatValue;
                    cellValueChanged(params.node, column.field);
                  },
                  decimalScale: 0,
                };
              },
            };
          }
          if (column.field === "max" || column.field === "commissionPercentage") {
            return {
              ...column,
              cellEditor: "cellNumberEditor",
              cellEditorParams: (params) => {
                return {
                  value: params?.value || 0,
                  onChange: (values) => {
                    params.node.data[column.field] = values.floatValue;
                    cellValueChanged(params.node, column.field);
                  },
                  decimalScale: 0,
                };
              },
            };
          }
          return column;
        });
        api.setColumnDefs([...newGridColumns]);
      }
    }, [
      api,
      cellValueChanged,
      doctorCommissionProgramGridRef,
      doctorCommissionProgramRowData,
      dispatch,
      gridColumns,
    ]);
  
    useEffect(() => {
      if (currentComponent !== "grid_doctor_commission_program") {
        gridEditingStopped();
      }
    }, [currentComponent, gridEditingStopped]);
  
    useEffect(() => {
      if (currentComponent === "grid_doctor_commission_program") {
        document.addEventListener("keyup", gridListener, false);
      }
      return () => {
        document.removeEventListener("keyup", gridListener, false);
      };
    }, [currentComponent, gridListener]);
    return (
      <>
        <div
          id="purchase_product_container"
          className={`${classes["product-grid-parent"]} ${classes["doctorCommissionProgram-bill-hide"]}`}
        >
          <div
            className={`${classes["product-grid-doctorCommissionProgram-bill-container"]}`}
          >
            <div className={`${classes["product-grid-container"]} `}>
              <div className={classes["product-grid"]}>
                <Aggrid
                  id="grid_doctor_commission_program"
                  ref={doctorCommissionProgramGridRef}
                  rowData={doctorCommissionProgramRowData}
                  defaultColDef={defaultColDef}
                  onGridReady={onGridReady}
                  frameworkComponents={{
                    cellTextEditor: CellTextEditor,
                    cellSelectInput: CellSelectInput,
                    cellNumberEditor: CellNumberEditor,
                    cellTreeSelect: CellTreeSelect,
                  }}
                  onCellFocused={(params) => {
                    setCurrentComponent("grid_doctor_commission_program");
                  }}
                />
              </div>
              <div className={classes["product-grid-controls"]}>
                <div
                  className={classes["product-grid-control-columns-start"]}
                ></div>
                <div className={classes["product-grid-control-columns-center"]}>
                  <div
                    className={
                      classes["product-grid-control-columns-center-button"]
                    }
                  >
                    <Button
                      label="ADD"
                      ref={addClickedRef}
                      startIcon={<Shortcut keys={["+"]} />}
                      onClick={addProgram}
                    />
                  </div>
                  <div
                    className={
                      classes["product-grid-control-columns-center-button"]
                    }
                  >
                    <Button
                      label="Delete"
                      startIcon={<Shortcut keys={["Del"]} />}
                      onClick={removeProduct}
                    />
                  </div>
                </div>
                <div
                  className={classes["product-grid-control-columns-end"]}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };
  
  export default DoctorCommissionProgramGrid;
  